import React, { useState } from 'react';
import { Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getRedirectPath } from '../utils/getRedirectPath';

import AppStoreBadge from '../components/AppStoreBadge';
import GooglePlayBadge from '../components/GooglePlayBadge';
import { isNativeApp } from '../utils/isNativeApp';

// 🔑 THE DEEP LINK SURVIVES THE LOGIN (Jesse, 2026-10-19). RequireAuth hands the page the borrower was opening
// (path + query + hash); after signing in they go there, and the route's own guard decides the rest — a guided
// borrower's /documents opens the in-shell Documents. Only an in-app path is honoured: never another origin, never
// a protocol-relative URL, never /login itself.
export function safeReturnPath(from) {
  const f = typeof from === 'string' ? from : '';
  if (!f.startsWith('/') || f.startsWith('//') || f.startsWith('/\\') || /^\/login(\b|\/|\?|#|$)/.test(f)) return null;
  return f;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = safeReturnPath(location.state && location.state.from);
  const { login, user, authLoading } = useAuth();
  // mode = 'login' | 'forgot' | 'forgot-sent'. forgot-sent is the silent
  // success state shown after POST /forgot-password regardless of whether
  // the email was on file (anti-enumeration).
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '' });
  const [serverError, setServerError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotSending, setForgotSending] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setServerError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      setServerError('Please enter your email and password.');
      return;
    }
    setLoading(true);
    setServerError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/login`, {
        email: form.email.trim(),
        password: form.password,
      });
      login(res.data.token, res.data.user);
      try {
        const statusRes = await axios.get(`${API_URL}/api/users/me/status`, {
          headers: { Authorization: `Bearer ${res.data.token}` },
        });
        navigate(returnTo || getRedirectPath(statusRes.data.application_status, statusRes.data.current_step));
      } catch {
        navigate(returnTo || '/dashboard');
      }
    } catch (err) {
      setServerError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setServerError('');
    if (!form.email.trim()) { setServerError('Please enter your email.'); return; }
    setForgotSending(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: form.email.trim() });
      // Backend always returns success — anti-enumeration. Same UX either way.
      setMode('forgot-sent');
    } catch (err) {
      setServerError(err.response?.data?.error || 'Could not send reset email. Please try again.');
    } finally {
      setForgotSending(false);
    }
  };

  const inputClass = `w-full h-14 px-4 rounded-xl border border-gray-200 hover:border-gray-300 text-base text-black placeholder-gray-400 bg-white outline-none transition-all focus:ring-2 focus:ring-[#3a6ad9]/30 focus:border-[#3a6ad9]`;

  // Auth-aware entry: if a session was already restored (durable token → user set),
  // don't show the login form over a live session — forward into the app. Gated on
  // authLoading so there's no redirect before restore resolves. A logged-out visitor
  // falls through to the form. (The fresh-login submit above navigates via
  // getRedirectPath after /me/status; that later navigation is the authority for the
  // just-logged-in flow.)
  if (authLoading) return null;
  if (user) return <Navigate to={returnTo || '/dashboard'} replace />;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <img src={`${process.env.PUBLIC_URL}/doorly_logo_black_icon.png`} alt="Doorly" style={{ height: '40px', width: 'auto' }} />
          </Link>
          <h1 className="text-2xl font-bold text-black mt-6 mb-1">
            {mode === 'login' ? 'Welcome back!' : 'Reset your password'}
          </h1>
          <p className="text-gray-500 text-sm">
            {mode === 'login'
              ? 'Log in to continue your home purchase application.'
              : mode === 'forgot'
                ? "Enter your email and we'll send you a reset link."
                : 'Check your email for a reset link.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-5 sm:p-8">
          {serverError && (
            <div className="mb-5 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              {serverError}
            </div>
          )}

          {mode === 'login' && (
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              <input name="email" type="email" className={inputClass} placeholder="you@example.com" value={form.email} onChange={handleChange} autoComplete="email" />
            </div>

            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input name="password" type="password" className={inputClass} placeholder="Your password" value={form.password} onChange={handleChange} autoComplete="current-password" />
            </div>

            <div className="text-right mb-6">
              <button type="button" onClick={() => { setServerError(''); setMode('forgot'); }}
                className="text-sm hover:underline" style={{ color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary btn-full text-white gap-2 hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: '#3a6ad9' }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Logging In...
                </>
              ) : (
                <>
                  <span>Log In</span>
                  <span className="btn-arrow">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
                  </span>
                </>
              )}
            </button>
          </form>
          )}

          {mode === 'forgot' && (
          <form onSubmit={handleForgot} noValidate>
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
              <input name="email" type="email" className={inputClass} placeholder="you@example.com" value={form.email} onChange={handleChange} autoComplete="email" autoFocus />
            </div>
            <button type="submit" disabled={forgotSending}
              className="btn-primary btn-full text-white hover:opacity-90 disabled:opacity-60 mb-3"
              style={{ backgroundColor: '#3a6ad9' }}>
              {forgotSending ? 'Sending…' : 'Send Reset Link'}
            </button>
            <button type="button" onClick={() => { setServerError(''); setMode('login'); }}
              className="w-full text-center text-sm hover:underline"
              style={{ color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer' }}>
              Back to login
            </button>
          </form>
          )}

          {mode === 'forgot-sent' && (
          <div className="space-y-4 text-center">
            <p className="text-sm text-gray-700 leading-relaxed">
              If <strong>{form.email}</strong> is on file, we sent a reset link. The link expires in 1 hour.
            </p>
            <p className="text-xs text-gray-500">Didn't get it? Check your spam folder, or try again in a minute.</p>
            <button type="button" onClick={() => { setServerError(''); setMode('login'); }}
              className="btn-primary btn-full text-white"
              style={{ backgroundColor: '#3a6ad9' }}>
              Back to login
            </button>
          </div>
          )}
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">
          Don't have an account?{' '}
          <Link to="/pre-qualify" className="font-semibold" style={{ color: '#3a6ad9' }}>
            Register here
          </Link>
        </p>

        {/* Download badges — web app only; hidden inside the native app */}
        {!isNativeApp() && (
        <div className="flex flex-col items-center" style={{ marginTop: 16 }}>
          <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 6 }}>Access your account from anywhere</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <AppStoreBadge />
            <GooglePlayBadge />
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export default Login;
